import axios, { AxiosRequestConfig } from 'axios';

import { endpoints, SEATERS_API_BASE_URL, V2Prefix } from '@seaters/constants';
import {
  clearSessionData,
  getSessionToken,
  setSessionData,
} from '../storage/sessionStorage';

const loginEndpoints = [
  endpoints.login,
  endpoints.login_v2,
  endpoints.refreshToken,
  `${V2Prefix}/${endpoints.authentication}/${endpoints.verifyOauth}`,
];
const isLoggedInEndpoint = (responseURL: string) =>
  loginEndpoints.find((endpoint) => responseURL.includes(endpoint));

const config: AxiosRequestConfig = { baseURL: SEATERS_API_BASE_URL };

export const api = axios.create(config);

let isRedirectingAfterUnauthorized = false;

// Outside /admin the recovery for a 401 is a reload of the *current* URL, so a 401
// that a reload cannot fix reloads forever. An in-memory guard cannot stop that —
// the reload resets the module — so the attempt is remembered in sessionStorage and
// allowed once per URL per tab. Keyed by URL rather than a bare flag so navigating
// somewhere else still gets its own attempt.
const UNAUTHORIZED_RELOAD_URL_KEY = 'seaters.unauthorized-reload-url';

const readAttemptedReloadUrl = (): string | null => {
  try {
    return sessionStorage.getItem(UNAUTHORIZED_RELOAD_URL_KEY);
  } catch {
    return null;
  }
};

const rememberAttemptedReloadUrl = (url: string): boolean => {
  try {
    sessionStorage.setItem(UNAUTHORIZED_RELOAD_URL_KEY, url);
    return true;
  } catch {
    return false;
  }
};

// Cleared only when the session is genuinely re-established, never on an arbitrary
// 2xx. The invitation app is the reason: with a rejected guest code its fan-group
// call returns 200 while the waiting-list call 401s, so clearing on any success
// would hand the loop a fresh attempt on every pass.
export const clearUnauthorizedReloadMarker = () => {
  try {
    sessionStorage.removeItem(UNAUTHORIZED_RELOAD_URL_KEY);
  } catch {
    // A browser that refuses session storage cannot loop via this path anyway,
    // because the reload is never attempted without a successful write.
  }
};

export const clearInvalidSession = () => {
  // Routed through the store so subscribed route guards re-render on logout
  // instead of holding a stale token until something else re-renders them.
  clearSessionData();
  delete axios.defaults.headers.common['Authorization'];
};

/**
 * Where a 401 should send the browser, or `null` to stay put.
 *
 * Exported for tests: the bug this replaces was a control-flow error that no type
 * or lint check could see, and it is only observable across a page load.
 *
 * - `/admin` goes to sign-in, a different URL, so it terminates on its own.
 * - Anywhere else the only recovery is reloading the current URL, which is why an
 *   unfixable 401 looped. `attemptedUrl` is the URL a reload was already spent on,
 *   read back from session storage, so the second pass declines.
 */
export const resolveUnauthorizedRedirect = (
  currentHref: string,
  currentPathname: string,
  attemptedUrl: string | null
): string | null => {
  if (currentPathname.startsWith('/admin')) return '/admin/sign-in';
  if (attemptedUrl === currentHref) return null;
  return currentHref;
};

const redirectAfterUnauthorized = () => {
  if (isRedirectingAfterUnauthorized) return;

  clearInvalidSession();

  const target = resolveUnauthorizedRedirect(
    window.location.href,
    window.location.pathname,
    readAttemptedReloadUrl()
  );
  if (target === null) return;

  // A reload of the current URL has to be recorded before it happens, and if it
  // cannot be recorded it cannot be bounded — so skip it rather than risk the loop.
  // Costs a stale-token recovery where site data is blocked; the better failure.
  if (
    target === window.location.href &&
    !rememberAttemptedReloadUrl(window.location.href)
  ) {
    return;
  }

  isRedirectingAfterUnauthorized = true;
  window.location.replace(target);
};

export const setAuthToken = () => {
  axios.defaults.headers.common['Authorization'] =
    'SeatersBearer ' + getSessionToken();
};

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    const token = getSessionToken();
    if (token && config.url !== 'v2/authentication/refresh-token/') {
      config.headers = config.headers ?? {};
      config.headers['Authorization'] = 'SeatersBearer ' + token;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

//Add a response interceptor

api.interceptors.response.use(
  (response) => {
    if (
      response.status === 200 &&
      isLoggedInEndpoint(response.request.responseURL)
    ) {
      setSessionData(response.data);

      setAuthToken();

      // The session is valid again, so a future 401 on this URL deserves its own
      // reload attempt.
      clearUnauthorizedReloadMarker();

      return response.data;
    }

    return response.data;
  },
  (error) => {
    const originalRequest = error.config ?? {};
    const unauthorizedSessionRequest =
      originalRequest.url?.includes('fan-short-session') ?? false;

    if (
      error.response?.status === 401 &&
      isLoggedInEndpoint(originalRequest.url ?? '')
    ) {
      return Promise.reject(error);
    }

    if (unauthorizedSessionRequest && error.response?.status === 401) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      redirectAfterUnauthorized();

      // return axios
      //   .post('/auth/token', {
      //     refresh_token: refreshToken,
      //   })
      //   .then((res) => {
      //   });
    }
    return Promise.reject(error);
  }
);
